import React, { useState } from 'react';
import { Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { apiService } from '../../services/api';

interface AlterarSenhaProps {
  onComplete: () => void;
  onCancel: () => void;
}

const AlterarSenha: React.FC<AlterarSenhaProps> = ({ onComplete, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    atual: false,
    nova: false,
    confirmar: false
  });
  const [formData, setFormData] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmarSenha: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Limpar erro ao digitar
    setSuccess(''); // Limpar sucesso ao digitar
  };

  const toggleShowPassword = (field: 'atual' | 'nova' | 'confirmar') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.novaSenha !== formData.confirmarSenha) {
      setError('Confirmação de senha não confere');
      return;
    }

    if (formData.novaSenha.length < 6) {
      setError('Nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await apiService.alterarSenha(formData.senhaAtual, formData.novaSenha, formData.confirmarSenha);
      setSuccess('Senha alterada com sucesso!');
      setTimeout(() => onComplete(), 2000);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erro ao alterar senha. Tente novamente.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-center">
        <div className="card shadow-sm" style={{width: '400px'}}>
          <div className="card-body p-4">
            <div className="text-center mb-4">
              <div className="text-success mb-3">
                <Lock size={48} />
              </div>
              <h3 className="text-success mb-2">Alterar Senha</h3>
              <p className="text-muted">
                Digite sua senha atual e escolha uma nova senha
              </p>
            </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success" role="alert">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="senha-atual" className="form-label fw-medium">Senha Atual *</label>
              <div className="input-group">
                <input
                  id="senha-atual"
                  type={showPasswords.atual ? "text" : "password"}
                  className="form-control"
                  name="senhaAtual"
                  value={formData.senhaAtual}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => toggleShowPassword('atual')}
                  disabled={loading}
                >
                  {showPasswords.atual ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="nova-senha" className="form-label fw-medium">Nova Senha *</label>
              <div className="input-group">
                <input
                  id="nova-senha"
                  type={showPasswords.nova ? "text" : "password"}
                  className="form-control"
                  name="novaSenha"
                  value={formData.novaSenha}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => toggleShowPassword('nova')}
                  disabled={loading}
                >
                  {showPasswords.nova ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="form-text">
                Mínimo de 6 caracteres
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="confirmar-senha" className="form-label fw-medium">Confirmar Nova Senha *</label>
              <div className="input-group">
                <input
                  id="confirmar-senha"
                  type={showPasswords.confirmar ? "text" : "password"}
                  className="form-control"
                  name="confirmarSenha"
                  value={formData.confirmarSenha}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => toggleShowPassword('confirmar')}
                  disabled={loading}
                >
                  {showPasswords.confirmar ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="d-flex justify-content-between">
              <button 
                type="button" 
                className="btn btn-secondary btn-sm d-flex align-items-center gap-2" 
                onClick={onCancel}
                disabled={loading}
                style={{width: '40%'}}
              >
                <ArrowLeft size={14} />
                Voltar
              </button>
              <button
                type="submit"
                className="btn btn-success btn-sm"
                disabled={loading}
                style={{width: '40%'}}
              >
                {loading ? (
                  <>
                    <output className="spinner-border spinner-border-sm me-2"></output>
                    Alterando...
                  </>
                ) : (
                  <>
                    <Lock size={14} className="me-2" />
                    Alterar
                  </>
                )}
              </button>
            </div>
          </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlterarSenha;